import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import Dropzone from 'react-dropzone';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Input from '@mui/material/Input';
import Stack from '@mui/material/Stack';
import { styled } from '@mui/material/styles';
import { DataGridPro, GridColDef } from '@mui/x-data-grid-pro';
import PublishOutlinedIcon from '@mui/icons-material/PublishOutlined';
import AutorenewOutlinedIcon from '@mui/icons-material/AutorenewOutlined';
import { palette } from 'theme/palette';
import { TextField } from 'common/components/Inputs/TextField/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import { AutoHideSnackbar } from 'common/Snackbar/AutoHideSnackbar';
import { AlertTitle } from 'common/Snackbar/AlertTitle';
import { TabPanelWithErrorHandling } from 'common/components/AppLayout/Components/TabPanelWithErrorHandling';
import { DEFAULT_BORDER_RADIUS } from 'theme/constants';
import { getReconciliationHistory, ReconciliationHistoryEntry } from './api/reconciliationApi';
import { useReconciliationUpload } from './useReconciliationUpload';
import { useRootFontScale } from 'common/components/ConfigurableTable/Components/Base/useRootFontScale';

const HISTORY_QUERY_KEY = ['subscriptionStatusReconciliation', 'history'];

const DropAreaBox = styled(Box)({
  position: 'absolute',
  top: 0,
  width: '100%',
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: 'rgba(255, 255, 255, 0.5)',
  fontSize: '2rem',
});

const UploadCard = styled(Box)(({ theme }) => ({
  position: 'relative',
  backgroundColor: palette.grey[100],
  borderRadius: DEFAULT_BORDER_RADIUS,
  padding: theme.spacing(20),
  boxShadow: `0px 0px ${theme.spacing(5)} 0px #00000014, 0px ${theme.spacing(1)} ${theme.spacing(2)} 0px #00000014`,
}));

const ButtonRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'center',
  gap: theme.spacing(12),
  marginTop: theme.spacing(16),
}));

const FileNameField = styled(TextField)(({ theme }) => ({
  backgroundColor: 'white',
  minWidth: theme.spacing(300),
}));

const GridContainer = styled(Box)(({ theme }) => ({
  background: palette.grey[200],
  width: '100%',
  height: theme.spacing(400),
  marginTop: theme.spacing(16),
}));

const columnBaseOptions = {
  sortable: false,
  filterable: false,
  disableColumnMenu: true,
  resizable: false,
  disableReorder: true,
};

export const SubscriptionStatusReconciliationPageContent = () => {
  const { t } = useTranslation();
  const [dropAreaDisplayed, setDropAreaDisplayed] = useState(false);
  const fontScale = useRootFontScale();

  const {
    selectedFile,
    selectFile,
    uploadFile,
    uploading,
    uploadStatus,
    setUploadStatus,
    clear,
  } = useReconciliationUpload();

  const { data: history = [] } = useQuery<ReconciliationHistoryEntry[]>({
    queryKey: HISTORY_QUERY_KEY,
    queryFn: getReconciliationHistory,
  });

  const columns: GridColDef[] = [
    {
      field: 'fileName',
      flex: 2,
      renderHeader: () => (
        <Typography variant="text3" color="text.primary">
          {t('common.fileName')}
        </Typography>
      ),
      ...columnBaseOptions,
    },
    {
      field: 'processedAt',
      flex: 1.5,
      renderHeader: () => (
        <Typography variant="text3" color="text.primary">
          {t('admin.subscriptionStatusReconciliation.processedAt')}
        </Typography>
      ),
      valueFormatter: (value: string) => new Date(value).toLocaleString(),
      ...columnBaseOptions,
    },
    {
      field: 'processedBy',
      flex: 1.5,
      renderHeader: () => (
        <Typography variant="text3" color="text.primary">
          {t('admin.subscriptionStatusReconciliation.processedBy')}
        </Typography>
      ),
      ...columnBaseOptions,
    },
    {
      field: 'processedCount',
      flex: 1,
      renderHeader: () => (
        <Typography variant="text3" color="text.primary">
          {t('admin.subscriptionStatusReconciliation.processed')}
        </Typography>
      ),
      ...columnBaseOptions,
    },
    {
      field: 'skippedCount',
      flex: 1,
      renderHeader: () => (
        <Typography variant="text3" color="text.primary">
          {t('admin.subscriptionStatusReconciliation.skipped')}
        </Typography>
      ),
      ...columnBaseOptions,
    },
  ];

  return (
    <Box sx={{ p: 20, height: '100%', boxSizing: 'border-box' }}>
      <Dropzone
        noClick
        onDrop={(acceptedFiles) => {
          if (uploading) {
            return;
          }
          setDropAreaDisplayed(false);
          selectFile(acceptedFiles[0]);
        }}
        onDragEnter={() => {
          if (uploading) {
            return;
          }
          setDropAreaDisplayed(true);
        }}
        onDragLeave={() => setDropAreaDisplayed(false)}
      >
        {({ getRootProps, getInputProps }) => (
          <Box sx={{ position: 'relative' }} {...getRootProps()}>
            <input {...getInputProps()} />
            <UploadCard>
              <Typography variant="text2" color="text.secondary" sx={{ ml: 16 }}>
                {t('common.fileName')}
              </Typography>
              <FileNameField
                disabled
                value={
                  selectedFile?.name ||
                  t('admin.subscriptionStatusReconciliation.selectFile')
                }
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start" sx={{ ml: 12 }}>
                        <PublishOutlinedIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  },
                }}
                sx={{
                  '& input': selectedFile
                    ? { WebkitTextFillColor: `${palette.text.primary} !important` }
                    : {},
                }}
              />
              <ButtonRow>
                {!selectedFile && (
                  <>
                    <Typography variant="h5" color="text.secondary">
                      {t('admin.subscriptionStatusReconciliation.dragAndDropFileOr')}
                    </Typography>
                    <label htmlFor="reconciliation-file-input">
                      <Input
                        id="reconciliation-file-input"
                        type="file"
                        inputProps={{ accept: '.csv' }}
                        sx={{ display: 'none' }}
                        onChange={(event) => {
                          const fileInput = event.target as HTMLInputElement;
                          if (fileInput?.files?.[0]) {
                            selectFile(fileInput.files[0]);
                            event.target.value = '';
                          }
                        }}
                      />
                      <Button variant="contained" component="span" sx={{ px: 16, py: 10 }}>
                        {t('admin.subscriptionStatusReconciliation.browse')}
                      </Button>
                    </label>
                  </>
                )}
                {selectedFile && uploading && (
                  <Button variant="contained" component="span" sx={{ px: 40, py: 10 }}>
                    <AutorenewOutlinedIcon fontSize="small" />
                  </Button>
                )}
                {selectedFile && !uploading && (
                  <>
                    <Button
                      variant="text"
                      color="tertiary"
                      onClick={clear}
                      sx={{ px: 16, py: 10 }}
                    >
                      {t('admin.subscriptionStatusReconciliation.clear')}
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={<PublishOutlinedIcon />}
                      onClick={uploadFile}
                      sx={{ px: 16, py: 10 }}
                    >
                      {t('admin.subscriptionStatusReconciliation.upload')}
                    </Button>
                  </>
                )}
              </ButtonRow>
            </UploadCard>
            {dropAreaDisplayed && (
              <DropAreaBox>
                <Typography variant="h1" color="text.secondary">
                  {t('common.dropFile')}
                </Typography>
              </DropAreaBox>
            )}
          </Box>
        )}
      </Dropzone>

      <Typography variant="h5" sx={{ mt: 24, mb: 8 }} color="text.secondary">
        {t('admin.subscriptionStatusReconciliation.history')}
      </Typography>

      <GridContainer>
        <DataGridPro
          rows={history}
          columns={columns}
          hideFooter
          disableVirtualization
          columnHeaderHeight={42 * fontScale}
          getRowHeight={() => 52 * fontScale}
        />
      </GridContainer>

      <Stack>
        <AutoHideSnackbar
          open={!!uploadStatus?.title}
          severity={uploadStatus?.severity || 'success'}
          onClose={() => setUploadStatus(null)}
        >
          <AlertTitle>{uploadStatus?.title || ''}</AlertTitle>
          {uploadStatus?.description && (
            <Typography variant="text1" color="text.white">
              {uploadStatus.description}
            </Typography>
          )}
        </AutoHideSnackbar>
      </Stack>
    </Box>
  );
};

export const SubscriptionStatusReconciliationPage = () => {
  const { t } = useTranslation();
  return (
    <TabPanelWithErrorHandling
      name={t('admin.subscriptionStatusReconciliation.subscriptionStatusReconciliation')}
    >
      <SubscriptionStatusReconciliationPageContent />
    </TabPanelWithErrorHandling>
  );
};
